function parseDate(dateStr: Date | string): Date {
  if (dateStr instanceof Date) return dateStr;
  // Normalize "2026-07-07T12:00+02:00" → "2026-07-07T12:00:00+02:00"
  const normalized = dateStr?.replace(/(T\d{2}:\d{2})([-+Z])/, "$1:00$2");
  return new Date(normalized);
}

function toIcsDate(date: Date): string {
  return `${date.toISOString().replace(/[-:]/g, "").split(".")[0]}Z`;
}

function escapeIcsText(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

export interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  startDate: Date | string;
  durationMinutes?: number;
}

function getStartEnd({ startDate, durationMinutes = 60 }: CalendarEvent): { start: Date; end: Date } | null {
  const start = parseDate(startDate);
  if (isNaN(start.getTime())) return null;
  return { start, end: new Date(start.getTime() + durationMinutes * 60 * 1000) };
}

/** Opens Google Calendar's "add event" template pre-filled with the available event fields. */
export function getGoogleCalendarUrl(event: CalendarEvent): string | null {
  const range = getStartEnd(event);
  if (!range) return null;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title || "",
    dates: `${toIcsDate(range.start)}/${toIcsDate(range.end)}`,
  });
  if (event.description) params.set("details", event.description);
  if (event.location) params.set("location", event.location);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildOutlookComposeUrl(host: string, event: CalendarEvent): string | null {
  const range = getStartEnd(event);
  if (!range) return null;

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title || "",
    startdt: range.start.toISOString(),
    enddt: range.end.toISOString(),
  });
  if (event.description) params.set("body", event.description);
  if (event.location) params.set("location", event.location);

  return `https://${host}/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Opens Outlook.com's compose-event deeplink. This only recognizes a signed-in *personal*
 * Microsoft account (outlook.com/live/hotmail) — a work-or-school (Microsoft 365) session on
 * outlook.office.com is a separate login domain and gets bounced to a sign-in page here even
 * when the user is already signed into their work account elsewhere in the browser.
 */
export function getOutlookCalendarUrl(event: CalendarEvent): string | null {
  return buildOutlookComposeUrl("outlook.live.com", event);
}

/**
 * Opens the work-or-school (Microsoft 365) equivalent of `getOutlookCalendarUrl`, for users
 * signed into outlook.office.com rather than a personal Microsoft account.
 */
export function getOutlookWorkCalendarUrl(event: CalendarEvent): string | null {
  return buildOutlookComposeUrl("outlook.office.com", event);
}

/**
 * Builds a minimal RFC 5545 .ics file from the fields available on an event and triggers a
 * browser download. This is the universal fallback (Apple Calendar has no web "add event" URL
 * scheme) — on iOS/macOS Safari the file opens directly in Calendar.app instead of downloading.
 */
export function downloadEventIcs(event: CalendarEvent): void {
  const range = getStartEnd(event);
  if (!range) return;
  const { title, description, location } = event;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//DAMAC//Event Calendar//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${Date.now()}@damacproperties.com`,
    `DTSTAMP:${toIcsDate(new Date())}`,
    `DTSTART:${toIcsDate(range.start)}`,
    `DTEND:${toIcsDate(range.end)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    description ? `DESCRIPTION:${escapeIcsText(description)}` : "",
    location ? `LOCATION:${escapeIcsText(location)}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);

  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${title || "event"}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
